import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { Plus } from "lucide-react"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { toastApiError } from "../../../api/apiError"
import {
  useArticleSuppliers,
  useUpdateArticleSupplier,
} from "../../../api/articlesApi"
import type { SupplierArticleRow } from "../../../api/types/masterData"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"
import { McPill } from "../../shared/McPill"
import { LinkSupplierToArticleDialog } from "./LinkSupplierToArticleDialog"

function ActiveCell({ row }: { row: SupplierArticleRow }) {
  const { translate } = useTranslation()
  const { mutate, isPending } = useUpdateArticleSupplier()
  return (
    <button
      type="button"
      disabled={isPending}
      onClick={(e) => {
        e.stopPropagation()
        mutate(
          {
            articleId: row.articleId,
            supplierId: row.supplierId,
            changes: { active: !row.active },
          },
          {
            onError: (err) => {
              toastApiError(err, translate, "MC_MD_SAVE_ERROR")
            },
          },
        )
      }}
    >
      {row.active ? (
        <McPill tone="success">{translate("MC_MD_PILL_ACTIVE")}</McPill>
      ) : (
        <McPill tone="default">{translate("MC_MD_PILL_INACTIVE")}</McPill>
      )}
    </button>
  )
}

export function ArticleSuppliersSection() {
  const { translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { hasPermission } = useUser()
  const canEdit = hasPermission(PERMISSIONS.MC_MASTER_DATA_EDIT)
  const { data: links = [], isLoading } = useArticleSuppliers(id)

  const columns: SubTableColumn<SupplierArticleRow>[] = [
    {
      key: "name",
      header: translate("MC_MD_PANEL_COL_SUPPLIER"),
      cell: (r) => r.supplierName ?? r.supplierId,
    },
    {
      key: "sku",
      header: translate("MC_MD_PANEL_COL_SUPPLIER_ARTICLE"),
      cell: (r) =>
        r.supplierArticleNumber ? (
          <span className="font-mono">{r.supplierArticleNumber}</span>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-32",
      hideBelow: "360px",
    },
    {
      key: "active",
      header: translate("MC_MD_PANEL_COL_STATUS"),
      cell: (r) => <ActiveCell row={r} />,
      className: "w-24",
      hideBelow: "280px",
    },
  ]

  const addButton = canEdit ? (
    <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
      <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
        <Plus />
        {translate("MC_MD_PANEL_SUPPLIERS_LINK")}
      </Button>
    </PermissionGate>
  ) : undefined

  return (
    <>
      {!isLoading && links.length === 0 && canEdit && (
        <p className="px-4 pt-3 text-[12px] text-grey5">
          {translate("MC_MD_PANEL_SUPPLIERS_EMPTY_HINT")}
        </p>
      )}
      <EntitySubTable
        title={translate("MC_MD_PANEL_SUPPLIERS_TITLE")}
        items={links}
        columns={columns}
        keyOf={(r) => r.id}
        hasMore={false}
        onFetchMore={() => Promise.resolve()}
        isLoading={isLoading}
        total={isLoading ? undefined : links.length}
        onRowClick={(r) =>
          navigate(`../suppliers?id=${r.supplierId}&source=nav`)
        }
        action={addButton}
      />
      {id && (
        <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
          <LinkSupplierToArticleDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            articleId={id}
          />
        </PermissionGate>
      )}
    </>
  )
}
